import { useState, useEffect, useRef } from 'react';
import { chatsApi } from '../../../api/chats';
import { normalizeReactions } from '../../../utils/reactions';
import {
    subscribeToChat,
    subscribeToTyping,
    subscribeToPinned,
    subscribeToPolls,
    subscribeToWebSocketErrors,
    sendMessage as wsSendMessage,
    sendTyping,
    joinChat,
    leaveChat,
} from '../../../api/websocket';
import { notifyNewMessage } from '../../../utils/chatNotifications';

/**
 * Хук WebSocket-подписок для чата: входящие сообщения, typing, join/leave.
 *
 * @param {Object} params
 * @param {string} params.id — ID чата
 * @param {Object} params.currentUser — текущий пользователь из UserContext
 * @param {boolean} params.wsConnected — подключён ли WebSocket
 * @param {string} [params.currentProfileId] — ID профиля текущего пользователя
 * @param {Function} params.setMessages — setter сообщений из useChatMessages
 * @param {React.MutableRefObject} params.pendingMessagesRef — ref optimistic-сообщений
 * @param {React.MutableRefObject} params.lastMessageCountRef — ref счётчика сообщений
 * @param {Function} params.scrollToBottom — функция прокрутки вниз
 * @param {React.MutableRefObject} params.isAtBottomRef — ref флага «пользователь внизу»
 * @param {Function} params.setNewMessagesCount — setter счётчика новых сообщений
 * @param {Function} params.setError — setter ошибок
 * @param {Function} [params.onNewMessage] — колбэк при получении нового сообщения от другого пользователя (для обновления списка чатов)
 * @returns {{ typingUsers: Set<string>, sendMessageWs: Function, sendTypingWs: Function }}
 */
export function useChatWebSocket({
    id,
    currentUser,
    wsConnected,
    currentProfileId,
    setMessages,
    pendingMessagesRef,
    lastMessageCountRef,
    scrollToBottom,
    isAtBottomRef,
    setNewMessagesCount,
    setError,
    onNewMessage,
    onPinnedUpdate,
    onPollUpdate,
}) {
    const [typingUsers, setTypingUsers] = useState(new Set());
    const messageSubscriptionRef = useRef(null);
    const typingSubscriptionRef = useRef(null);
    const pinnedSubscriptionRef = useRef(null);
    const pollsSubscriptionRef = useRef(null);
    const errorSubscriptionRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const markAsReadTimerRef = useRef(null);

    // Ref для доступа к currentUser без пересоздания useEffect
    const currentUserRef = useRef(currentUser);
    useEffect(() => {
        currentUserRef.current = currentUser;
    }, [currentUser]);

    // Подписка на сообщения и typing через WebSocket.
    // initWebSocket / disconnectWebSocket управляются ChatsLayout —
    // здесь только подписываемся и отписываемся.
    useEffect(() => {
        if (!id || !currentUser || !wsConnected) return;

        let isMounted = true;

        // Подписываемся на сообщения чата
        messageSubscriptionRef.current = subscribeToChat(id, (message) => {
            if (!isMounted) return;

            // Пользователь смотрит чат — помечаем входящее сообщение прочитанным
            const myProfileId = currentUserRef.current?.profileId
                || currentUserRef.current?.profiles?.[0]?.id;
            if (myProfileId) {
                const seenBy = message.seenBy || [];
                const alreadyRead = seenBy.some(s => {
                    const seenId = typeof s === 'string' ? s : (s?.id || s?.userId || s?.profileId);
                    return seenId === myProfileId;
                });
                if (!alreadyRead) {
                    message.seenBy = [...seenBy, myProfileId];
                }
                // Debounced markAsRead — не вызываем REST на каждое входящее сообщение,
                // а батчим запросы раз в 2 секунды
                if (markAsReadTimerRef.current) {
                    clearTimeout(markAsReadTimerRef.current);
                }
                markAsReadTimerRef.current = setTimeout(() => {
                    markAsReadTimerRef.current = null;
                    chatsApi.markAsReadForProfile(id, myProfileId).catch(() => {});
                }, 2000);
            }

            setMessages(prev => {
                const exists = prev.find(m => m.id === message.id);
                if (exists) return prev;

                // Проверяем, является ли это эхом нашего optimistic-сообщения.
                // Сначала по senderId, затем — по совпадению содержимого с pending.
                const myProfileId = currentUserRef.current?.profileId
                    || currentUserRef.current?.profiles?.[0]?.id;
                const myUserId = currentUserRef.current?.id;

                // ID отправителя из WS-сообщения: может быть profileId или userId
                const msgSenderId = message.senderId
                    || (typeof message.sender === 'object'
                        ? (message.sender.id || message.sender.userId || message.sender.profileId)
                        : null);
                const msgUserId = message.userId || message.authorId
                    || (typeof message.sender === 'object' ? message.sender.userId : null);

                // Сообщение от нас — заменяем первое ожидающее optimistic
                const isOwnMessage = (myProfileId && msgSenderId === myProfileId)
                    || (myUserId && (msgUserId === myUserId || msgSenderId === myUserId));

                // Fallback: если есть pending-сообщения, проверяем совпадение по содержимому.
                // Сервер может прислать senderId в другом формате (user ID вместо profile ID),
                // из-за чего isOwnMessage = false и сообщение добавляется дублем.
                const msgContent = message.content || message.text || '';
                if (pendingMessagesRef.current.length > 0) {
                    // Сначала пробуем по senderId
                    if (isOwnMessage) {
                        const pendingEntry = pendingMessagesRef.current.shift();
                        const normalizedMsg = { ...message, reactions: normalizeReactions(message.reactions) };
                        return prev.map(m => m.id === pendingEntry.tempId ? normalizedMsg : m).sort((a, b) =>
                            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                        );
                    }

                    // Fallback: ищем pending с таким же содержимым
                    const matchIdx = pendingMessagesRef.current.findIndex(
                        p => p.content === msgContent
                    );
                    if (matchIdx >= 0) {
                        const [pendingEntry] = pendingMessagesRef.current.splice(matchIdx, 1);
                        const normalizedMsg = { ...message, reactions: normalizeReactions(message.reactions) };
                        return prev.map(m => m.id === pendingEntry.tempId ? normalizedMsg : m).sort((a, b) =>
                            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                        );
                    }
                }

                const normalizedMsg = { ...message, reactions: normalizeReactions(message.reactions) };
                const newMessages = [...prev, normalizedMsg].sort((a, b) =>
                    new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                );

                lastMessageCountRef.current = newMessages.length;
                return newMessages;
            });

            // Скроллим вниз только если пользователь уже был внизу.
            // Иначе — инкрементим счётчик новых сообщений для плавающей кнопки.
            if (isAtBottomRef.current) {
                setTimeout(scrollToBottom, 100);
            } else {
                setNewMessagesCount(prev => prev + 1);
            }

            // Звуковое + браузерное уведомление при неактивной вкладке
            // и сообщении от другого пользователя
            const notifSenderId = message.senderId
                || (typeof message.sender === 'object'
                    ? (message.sender.id || message.sender.userId || message.sender.profileId)
                    : null);
            const myNotifProfileId = currentUserRef.current?.profileId
                || currentUserRef.current?.profiles?.[0]?.id;

            if (myNotifProfileId && notifSenderId !== myNotifProfileId) {
                const senderName = (typeof message.sender === 'object'
                    ? (message.sender?.displayName || message.sender?.name)
                    : null) || message.senderDisplayName || 'Собеседник';
                const preview = message.isDeleted
                    ? '[Сообщение удалено]'
                    : (message.content || message.text || '[Вложение]');

                notifyNewMessage({
                    senderName,
                    preview,
                    chatId: id,
                });

                // Обновляем список чатов в сайдбаре (последнее сообщение, порядок, unread)
                if (typeof onNewMessage === 'function') {
                    onNewMessage(message);
                }
            }
        });

        // Подписываемся на события typing
        typingSubscriptionRef.current = subscribeToTyping(id, (typingEvent) => {
            if (!isMounted) return;

            if (typingEvent.profileId && typingEvent.displayName) {
                const myProfileId = currentUserRef.current?.profileId || currentUserRef.current?.profiles?.[0]?.id;
                if (myProfileId && typingEvent.profileId === myProfileId) {
                    return;
                }

                // isTyping: false — немедленно убираем из списка печатающих
                if (typingEvent.isTyping === false) {
                    setTypingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(typingEvent.displayName);
                        return next;
                    });
                    return;
                }

                setTypingUsers(prev => new Set([...prev, typingEvent.displayName]));

                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }

                typingTimeoutRef.current = setTimeout(() => {
                    setTypingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(typingEvent.displayName);
                        return next;
                    });
                }, 3000);
            }
        });

        // Подписываемся на события закрепления/открепления сообщений
        if (onPinnedUpdate) {
            pinnedSubscriptionRef.current = subscribeToPinned(id, (pinnedEvent) => {
                if (!isMounted) return;
                onPinnedUpdate(pinnedEvent);
            });
        }

        // Подписываемся на обновления опросов (голосование, закрытие)
        if (onPollUpdate) {
            pollsSubscriptionRef.current = subscribeToPolls(id, (pollEvent) => {
                if (!isMounted) return;
                onPollUpdate(pollEvent);
            });
        }

        // Уведомляем о входе в чат
        joinChat(id);

        // Подписываемся на ошибки WebSocket (мут, валидация и т.д.)
        errorSubscriptionRef.current = subscribeToWebSocketErrors((errorData) => {
            if (!isMounted) return;
            const message = errorData?.message || errorData?.error || '';
            if (/muted/i.test(message)) {
                setError('Вы не можете отправлять сообщения — на вас наложен мут (жёлтая карточка).');
            } else {
                setError(message || 'Ошибка при отправке сообщения');
            }
        });

        return () => {
            isMounted = false;

            if (markAsReadTimerRef.current) {
                clearTimeout(markAsReadTimerRef.current);
                markAsReadTimerRef.current = null;
            }

            if (messageSubscriptionRef.current) {
                messageSubscriptionRef.current.unsubscribe();
                messageSubscriptionRef.current = null;
            }
            if (typingSubscriptionRef.current) {
                typingSubscriptionRef.current.unsubscribe();
                typingSubscriptionRef.current = null;
            }
            if (pinnedSubscriptionRef.current) {
                pinnedSubscriptionRef.current.unsubscribe();
                pinnedSubscriptionRef.current = null;
            }
            if (pollsSubscriptionRef.current) {
                pollsSubscriptionRef.current.unsubscribe();
                pollsSubscriptionRef.current = null;
            }
            if (errorSubscriptionRef.current) {
                errorSubscriptionRef.current.unsubscribe();
                errorSubscriptionRef.current = null;
            }
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
            }

            leaveChat(id);
        };
    }, [id, currentUser, wsConnected, setMessages, setError, pendingMessagesRef, lastMessageCountRef, scrollToBottom, isAtBottomRef, setNewMessagesCount, onNewMessage, onPinnedUpdate, onPollUpdate]);

    // Сброс typing при смене чата
    useEffect(() => {
        setTypingUsers(new Set());
    }, [id]);

    /**
     * Отправка текстового сообщения через WebSocket.
     */
    const sendMessageWs = (text) => {
        return wsSendMessage(id, text);
    };

    /**
     * Отправка typing-события через WebSocket.
     */
    const sendTypingWs = (profileId, displayName) => {
        sendTyping(id, { profileId, displayName }, true);
    };

    /**
     * Отправка события остановки набора текста через WebSocket.
     * Немедленно очищает индикатор "печатает" у собеседника.
     */
    const sendStopTypingWs = (profileId, displayName) => {
        sendTyping(id, { profileId, displayName }, false);
    };

    return { typingUsers, sendMessageWs, sendTypingWs, sendStopTypingWs };
}
